import { z } from 'zod';
/**
 * Hộp thư thông báo — APM-NOTI-b, docs/plan-notifications.md.
 *
 * Kênh SSE (`GET /stream`) chỉ đẩy **tín hiệu** "có thay đổi"; nội dung thật đi
 * qua những endpoint ở đây, tức là qua guard và repository có scope. Hai đường
 * tách bạch có chủ đích — xem docs/api-conventions.md §8.
 */
export const NotificationView = z.object({
    id: z.uuid(),
    /** `comment.mentioned`, `task.assigned`, ... — trùng tên với event tương ứng. */
    type: z.string(),
    /**
     * Khoá i18n + tham số, **không phải chữ đã dựng sẵn**.
     *
     * Ngôn ngữ quyết định lúc đọc: hai người trong cùng tổ chức đọc cùng một
     * thông báo bằng hai thứ tiếng, và người vừa đổi ngôn ngữ phải thấy cả hộp
     * thư cũ đổi theo.
     */
    titleKey: z.string(),
    params: z.record(z.string(), z.unknown()),
    /**
     * Số lần sự việc lặp lại. `1` với thông báo không gộp.
     *
     * Truyền thẳng vào `t()` làm biến `count`, nên khoá i18n của loại có gộp phải
     * khai dạng số nhiều (`_one`/`_other`); loại không gộp thì i18next tự rơi về
     * khoá gốc.
     */
    count: z.number().int().positive(),
    projectId: z.uuid().nullable(),
    /**
     * Đích deep-link, dạng `entityType` + `entityId` chứ không phải một đường dẫn
     * đã dựng sẵn: một route đã lưu là ảnh chụp bảng route tại thời điểm ghi, mà
     * hộp thư sống lâu hơn bảng route.
     *
     * ⚠️ `entityType` là **chuỗi tự do, không phải enum**, và frontend phải có
     * nhánh mặc định "không link" cho giá trị lạ. Loại mới xuất hiện theo từng
     * task (`task` ở APM-NOTI-a, rồi `membership_request`, `announcement`...), nên
     * một client cũ đọc hộp thư của server mới là chuyện bình thường — nó phải
     * hiện thông báo đó không kèm link, không phải vỡ cả trang.
     */
    entityType: z.string().nullable(),
    entityId: z.uuid().nullable(),
    /**
     * Đích của thông báo **không còn** — dự án, việc, lỗi hay issue đã bị xoá
     * (kể cả xoá mềm). APM-NOTIGONE-a.
     *
     * Thông báo cố ý không có khoá ngoại tới đích (`prisma/schema.prisma`, model
     * `Notification`): nó là biên bản một việc đã xảy ra, sống lâu hơn thứ nó nói
     * về. Không có cờ này thì giao diện vẫn dựng link và người dùng bấm vào chỉ
     * gặp "Không tìm thấy nội dung" — hoặc tệ hơn, trang tự đổi sang dự án khác
     * và hiện sai nội dung. `true` ⇒ hiện không link. Vắng = còn (server cũ).
     */
    targetGone: z.boolean().optional(),
    /** Ai gây ra. `null` với thông báo do hệ thống sinh (job quét, ngân sách AI). */
    actorId: z.uuid().nullable(),
    readAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
});
export const NotificationQuery = z.object({
    /**
     * `id` của dòng cuối trang trước — docs/api-conventions.md §4.
     *
     * Danh sách sắp theo `(created_at, id)` giảm dần, và `id` là UUIDv7 nên nó
     * vừa duy nhất vừa cùng chiều thời gian. Nhờ vậy client chỉ phải mang một giá
     * trị thay vì cả cặp, mà phân trang vẫn đứng yên khi hai thông báo rơi vào
     * cùng một mili giây.
     */
    cursor: z.uuid().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(30),
    /**
     * ⚠️ `z.stringbool()` chứ không phải `z.coerce.boolean()`: cái sau ép **mọi**
     * chuỗi khác rỗng thành `true`, nên `?unreadOnly=false` sẽ có nghĩa ngược lại
     * với chữ đang viết trên URL.
     */
    unreadOnly: z.stringbool().default(false),
});
export const NotificationPage = z.object({
    items: z.array(NotificationView),
    page: z.object({
        cursor: z.uuid().nullable(),
        hasMore: z.boolean(),
        limit: z.number().int().positive(),
    }),
    /** Kèm luôn để badge không phải gọi thêm một vòng nữa. */
    unreadCount: z.number().int().nonnegative(),
});
/**
 * Bọc thành object thay vì trả số trần.
 *
 * Nest serialize giá trị trần thành body rỗng, `apiFetch` đọc body rỗng thành
 * `undefined`, và React Query từ chối `undefined` ⇒ hỏng cả query. Tệ nhất là
 * nó hỏng ở đúng trạng thái thường gặp nhất của endpoint này: chưa đọc bằng 0.
 */
export const UnreadCountView = z.object({
    count: z.number().int().nonnegative(),
});
// ── Gửi thủ công — APM-NOTI-j ───────────────────────────────────────────────
/**
 * Đối tượng của một lượt gửi.
 *
 * ## ⚠️ Client không bao giờ gửi danh sách người nhận
 *
 * Chỉ gửi **ý định** ("cả dự án này", "cấp dưới của tôi"); server tự giải ra
 * người nhận và luôn loại người gửi. Nhận danh sách từ client nghĩa là ai cũng
 * gửi được cho bất kỳ ai bằng một lời gọi tự chế — cửa quyền khi đó chỉ tồn tại
 * trong giao diện.
 */
export const AnnouncementAudience = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('project'), projectId: z.uuid() }),
    z.object({ kind: z.literal('direct_reports') }),
    z.object({ kind: z.literal('organization') }),
]);
/** Trần độ dài. Thông báo là một dòng nhắc, không phải một bài viết. */
export const CreateAnnouncementInput = z.object({
    audience: AnnouncementAudience,
    title: z.string().trim().min(1).max(120),
    body: z.string().trim().min(1).max(2_000),
});
export const AnnouncementView = z.object({
    id: z.uuid(),
    audience: z.enum(['project', 'direct_reports', 'organization']),
    projectId: z.uuid().nullable(),
    title: z.string(),
    body: z.string(),
    /** Chụp tại thời điểm gửi — xem `model Announcement`. */
    recipientCount: z.number().int().nonnegative(),
    senderId: z.uuid(),
    createdAt: z.iso.datetime(),
});
/**
 * Bao nhiêu người sẽ nhận — hiện **trước** khi bấm gửi (APM-NOTI-k).
 *
 * "Gửi cho toàn tổ chức" mà không biết đó là 12 hay 480 người là một nút không
 * ai dám bấm — hoặc tệ hơn, dám bấm mà không biết.
 */
export const AudienceSizeView = z.object({
    count: z.number().int().nonnegative(),
});
//# sourceMappingURL=notifications.js.map